'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { FilterCelebrities } from '@/components/atoms/FilterCelebrities';
import { CelebrityCardSkeleton } from '@/components/molecules/CelebrityCard';
import { PaginationControl } from '@/shared/components/Pagination/PaginationControl';
import { celebritiesListOptions } from './../../../../../entities/Celebrity/api/celebritiesList';
import Typography from '../../../../atoms/Typography';
import CelebritiesList from '../../organisms/CelebritiesList';
export type SortType = 'name' | 'newest' | 'audience';
const ITEMS_PER_PAGE = 8;
const HeroFrame = () => {
  const $ = _c(120);
  const t = useTranslations("Celebrities.OurCelebrities");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("name") || undefined;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const initialSearchValue = t0;
  let t1;
  bb0: {
    const sortByName = searchParams.get("sortByName");
    if (!sortByName) {
      t1 = undefined;
      break bb0;
    }
    t1 = sortByName === "asc" ? "asc" : "desc";
  }
  const initialSortByName = t1;
  let t2;
  bb1: {
    const sortByDate = searchParams.get("sortByDate");
    if (!sortByDate) {
      t2 = undefined;
      break bb1;
    }
    t2 = sortByDate === "asc" ? "asc" : "desc";
  }
  const initialSortByDate = t2;
  let t3;
  bb2: {
    let t4;
    if ($[2] !== searchParams) {
      t4 = searchParams.get("page");
      $[2] = searchParams;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    const pageParam = t4;
    if (!pageParam) {
      t3 = 1;
      break bb2;
    }
    const parsed = Number(pageParam);
    if (Number.isNaN(parsed) || parsed < 1) {
      t3 = 1;
      break bb2;
    }
    t3 = Math.floor(parsed);
  }
  const initialPage = t3;
  let t4;
  if ($[4] !== searchParams) {
    bb3: {
      const raw = searchParams.get("celebrities");
      if (!raw?.trim()) {
        let t5;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = [];
          $[6] = t5;
        } else {
          t5 = $[6];
        }
        t4 = t5;
        break bb3;
      }
      t4 = raw.split(",").map(_temp).filter(_temp2);
    }
    $[4] = searchParams;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const initialSelectedCelebrityIds = t4;
  const [searchValue, setSearchValue] = useState(initialSearchValue || "");
  const [selectedCelebrityIds, setSelectedCelebrityIds] = useState(initialSelectedCelebrityIds);
  const [sortByName_0, setSortByName] = useState(initialSortByName);
  const [sortByDate_0, setSortByDate] = useState(initialSortByDate);
  const [page, setPageState] = useState(initialPage);
  let t5;
  let t6;
  if ($[7] !== initialSearchValue || $[8] !== initialSelectedCelebrityIds || $[9] !== initialSortByDate || $[10] !== initialSortByName) {
    t5 = () => {
      setSearchValue(initialSearchValue || "");
      setSelectedCelebrityIds(initialSelectedCelebrityIds);
      setSortByName(initialSortByName);
      setSortByDate(initialSortByDate);
    };
    t6 = [initialSearchValue, initialSelectedCelebrityIds, initialSortByName, initialSortByDate];
    $[7] = initialSearchValue;
    $[8] = initialSelectedCelebrityIds;
    $[9] = initialSortByDate;
    $[10] = initialSortByName;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[13] !== initialPage) {
    t7 = () => {
      setPageState(initialPage);
    };
    t8 = [initialPage];
    $[13] = initialPage;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  const normalizeNameParam = _temp3;
  let t9;
  if ($[16] !== pathname || $[17] !== router) {
    t9 = (nextSortByName, nextSortByDate, nextName, nextPage, nextCelebrities) => {
      const params = new URLSearchParams();
      if (nextSortByName) {
        params.set("sortByName", nextSortByName);
      }
      if (nextSortByDate) {
        params.set("sortByDate", nextSortByDate);
      }
      if (nextName) {
        params.set("name", nextName);
      }
      if (nextCelebrities?.length) {
        params.set("celebrities", nextCelebrities.join(","));
      }
      if (nextPage > 1) {
        params.set("page", nextPage.toString());
      }
      const query = params.toString();
      router.push(query ? `${pathname}?${query}` : pathname);
    };
    $[16] = pathname;
    $[17] = router;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const pushQueryParams = t9;
  let t10;
  if ($[19] !== page || $[20] !== pushQueryParams || $[21] !== searchValue || $[22] !== selectedCelebrityIds || $[23] !== sortByDate_0) {
    t10 = nextSortByName_0 => {
      const normalizedName = normalizeNameParam(searchValue);
      const normalizedPage = Math.max(1, Math.floor(page));
      pushQueryParams(nextSortByName_0, sortByDate_0, normalizedName, normalizedPage, selectedCelebrityIds);
    };
    $[19] = page;
    $[20] = pushQueryParams;
    $[21] = searchValue;
    $[22] = selectedCelebrityIds;
    $[23] = sortByDate_0;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const setInitialSortByName = t10;
  let t11;
  if ($[25] !== page || $[26] !== pushQueryParams || $[27] !== searchValue || $[28] !== selectedCelebrityIds || $[29] !== sortByName_0) {
    t11 = nextSortByDate_0 => {
      const normalizedName_0 = normalizeNameParam(searchValue);
      const normalizedPage_0 = Math.max(1, Math.floor(page));
      pushQueryParams(sortByName_0, nextSortByDate_0, normalizedName_0, normalizedPage_0, selectedCelebrityIds);
    };
    $[25] = page;
    $[26] = pushQueryParams;
    $[27] = searchValue;
    $[28] = selectedCelebrityIds;
    $[29] = sortByName_0;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const setInitialSortByDate = t11;
  let t12;
  if ($[31] !== page || $[32] !== pushQueryParams || $[33] !== selectedCelebrityIds || $[34] !== sortByDate_0 || $[35] !== sortByName_0) {
    t12 = name => {
      const normalizedName_1 = normalizeNameParam(name);
      const normalizedPage_1 = Math.max(1, Math.floor(page));
      pushQueryParams(sortByName_0, sortByDate_0, normalizedName_1, normalizedPage_1, selectedCelebrityIds);
    };
    $[31] = page;
    $[32] = pushQueryParams;
    $[33] = selectedCelebrityIds;
    $[34] = sortByDate_0;
    $[35] = sortByName_0;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const setInitialSearchValue = t12;
  const [debouncedSearchValue] = useDebounceValue(searchValue, 500);
  const [debouncedSortByName] = useDebounceValue(sortByName_0, 500);
  const [debouncedSortByDate] = useDebounceValue(sortByDate_0, 500);
  let t13;
  let t14;
  if ($[37] !== debouncedSearchValue || $[38] !== initialSearchValue || $[39] !== setInitialSearchValue) {
    t13 = () => {
      const normalizedDebounced = debouncedSearchValue ?? "";
      const normalizedInitial = initialSearchValue ?? "";
      if (normalizedDebounced === normalizedInitial) {
        return;
      }
      setInitialSearchValue(debouncedSearchValue);
    };
    t14 = [debouncedSearchValue, setInitialSearchValue, initialSearchValue];
    $[37] = debouncedSearchValue;
    $[38] = initialSearchValue;
    $[39] = setInitialSearchValue;
    $[40] = t13;
    $[41] = t14;
  } else {
    t13 = $[40];
    t14 = $[41];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[42] !== debouncedSortByName || $[43] !== initialSortByName || $[44] !== setInitialSortByName) {
    t15 = () => {
      if (debouncedSortByName === initialSortByName) {
        return;
      }
      setInitialSortByName(debouncedSortByName);
    };
    t16 = [debouncedSortByName, setInitialSortByName, initialSortByName];
    $[42] = debouncedSortByName;
    $[43] = initialSortByName;
    $[44] = setInitialSortByName;
    $[45] = t15;
    $[46] = t16;
  } else {
    t15 = $[45];
    t16 = $[46];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[47] !== debouncedSortByDate || $[48] !== initialSortByDate || $[49] !== setInitialSortByDate) {
    t17 = () => {
      if (debouncedSortByDate === initialSortByDate) {
        return;
      }
      setInitialSortByDate(debouncedSortByDate);
    };
    t18 = [debouncedSortByDate, setInitialSortByDate, initialSortByDate];
    $[47] = debouncedSortByDate;
    $[48] = initialSortByDate;
    $[49] = setInitialSortByDate;
    $[50] = t17;
    $[51] = t18;
  } else {
    t17 = $[50];
    t18 = $[51];
  }
  useEffect(t17, t18);
  const t19 = debouncedSearchValue && debouncedSearchValue.length >= 2 ? debouncedSearchValue : undefined;
  let t20;
  if ($[52] !== selectedCelebrityIds || $[53] !== sortByDate_0 || $[54] !== sortByName_0 || $[55] !== t19) {
    t20 = celebritiesListOptions({
      sortByName: sortByName_0,
      sortByDate: sortByDate_0,
      name: t19,
      celebrities: selectedCelebrityIds
    });
    $[52] = selectedCelebrityIds;
    $[53] = sortByDate_0;
    $[54] = sortByName_0;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t20) {
    t21 = {
      ...t20
    };
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const {
    data: t22,
    isLoading,
    isFetching,
    error
  } = useQuery(t21);
  let t23;
  if ($[59] !== t22) {
    t23 = t22 === undefined ? [] : t22;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const celebritiesList = t23;
  const totalCelebrities = celebritiesList?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCelebrities / ITEMS_PER_PAGE));
  const activePage = Math.min(page, totalPages);
  let t24;
  let t25;
  if ($[61] !== page || $[62] !== pushQueryParams || $[63] !== searchValue || $[64] !== selectedCelebrityIds || $[65] !== sortByDate_0 || $[66] !== sortByName_0 || $[67] !== totalPages) {
    t24 = () => {
      if (page > totalPages) {
        const normalizedName_2 = normalizeNameParam(searchValue);
        setPageState(totalPages);
        pushQueryParams(sortByName_0, sortByDate_0, normalizedName_2, totalPages, selectedCelebrityIds);
      }
    };
    t25 = [page, totalPages, normalizeNameParam, pushQueryParams, searchValue, selectedCelebrityIds, sortByDate_0, sortByName_0];
    $[61] = page;
    $[62] = pushQueryParams;
    $[63] = searchValue;
    $[64] = selectedCelebrityIds;
    $[65] = sortByDate_0;
    $[66] = sortByName_0;
    $[67] = totalPages;
    $[68] = t24;
    $[69] = t25;
  } else {
    t24 = $[68];
    t25 = $[69];
  }
  useEffect(t24, t25);
  let t26;
  bb4: {
    if (!celebritiesList?.length) {
      t26 = celebritiesList;
      break bb4;
    }
    const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    let t27;
    if ($[70] !== celebritiesList || $[71] !== endIndex || $[72] !== startIndex) {
      t27 = celebritiesList.slice(startIndex, endIndex);
      $[70] = celebritiesList;
      $[71] = endIndex;
      $[72] = startIndex;
      $[73] = t27;
    } else {
      t27 = $[73];
    }
    t26 = t27;
  }
  const paginatedCelebrities = t26;
  let t27;
  if ($[74] !== page || $[75] !== pushQueryParams || $[76] !== searchValue || $[77] !== selectedCelebrityIds || $[78] !== sortByDate_0 || $[79] !== sortByName_0 || $[80] !== totalPages) {
    t27 = targetPage => {
      const nextPage_0 = Math.min(Math.max(1, targetPage), totalPages);
      if (nextPage_0 === page) {
        return;
      }
      setPageState(nextPage_0);
      const normalizedName_3 = normalizeNameParam(searchValue);
      pushQueryParams(sortByName_0, sortByDate_0, normalizedName_3, nextPage_0, selectedCelebrityIds);
      if (typeof window !== "undefined") {
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    };
    $[74] = page;
    $[75] = pushQueryParams;
    $[76] = searchValue;
    $[77] = selectedCelebrityIds;
    $[78] = sortByDate_0;
    $[79] = sortByName_0;
    $[80] = totalPages;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  const handlePageChange = t27;
  let t28;
  if ($[82] !== t) {
    t28 = t("Title");
    $[82] = t;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== t28) {
    t29 = <div className="mb-4 mt-6 md:mb-6 md:mt-8"><h1 className="font-montserrat text-[24px] font-extrabold uppercase leading-[1.1] tracking-[0.03em] text-white sm:text-[32px] md:text-[42px] md:leading-[1.2]">{t28}</h1></div>;
    $[84] = t28;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== page || $[87] !== pushQueryParams || $[88] !== searchValue || $[89] !== sortByDate_0 || $[90] !== sortByName_0) {
    t30 = ids => {
      setSelectedCelebrityIds(ids);
      if (ids.length === 0) {
        setSearchValue("");
      }
      const normalizedName_4 = ids.length ? normalizeNameParam(searchValue) : undefined;
      pushQueryParams(sortByName_0, sortByDate_0, normalizedName_4, page, ids);
    };
    $[86] = page;
    $[87] = pushQueryParams;
    $[88] = searchValue;
    $[89] = sortByDate_0;
    $[90] = sortByName_0;
    $[91] = t30;
  } else {
    t30 = $[91];
  }
  let t31;
  if ($[92] !== page || $[93] !== pushQueryParams || $[94] !== sortByDate_0 || $[95] !== sortByName_0) {
    t31 = option => {
      setSearchValue(option.label);
      setSelectedCelebrityIds([option.value]);
      pushQueryParams(sortByName_0, sortByDate_0, normalizeNameParam(option.label), page, [option.value]);
    };
    $[92] = page;
    $[93] = pushQueryParams;
    $[94] = sortByDate_0;
    $[95] = sortByName_0;
    $[96] = t31;
  } else {
    t31 = $[96];
  }
  let t32;
  if ($[97] !== searchValue || $[98] !== selectedCelebrityIds || $[99] !== sortByName_0 || $[100] !== t30 || $[101] !== t31) {
    t32 = <FilterCelebrities celebrities={selectedCelebrityIds} onCelebritiesChange={t30} celebrityName={searchValue} setCelebrityName={setSearchValue} onCelebritySelect={t31} sortByName={sortByName_0} setSortByName={setSortByName} />;
    $[97] = searchValue;
    $[98] = selectedCelebrityIds;
    $[99] = sortByName_0;
    $[100] = t30;
    $[101] = t31;
    $[102] = t32;
  } else {
    t32 = $[102];
  }
  let t33;
  if ($[103] !== t29 || $[104] !== t32) {
    t33 = <div className="flex flex-col">{t29}{t32}</div>;
    $[103] = t29;
    $[104] = t32;
    $[105] = t33;
  } else {
    t33 = $[105];
  }
  let t34;
  if ($[106] !== error || $[107] !== isFetching || $[108] !== isLoading || $[109] !== paginatedCelebrities) {
    t34 = isLoading || isFetching ? <div className="grid grid-cols-2 gap-1.5 md:gap-2 lg:gap-4 md:grid-cols-3 lg:grid-cols-4 mt-6 md:mt-8">{[1, 2, 3, 4].map(_temp4)}</div> : error ? <div className="mb-[48px] mt-[32px] flex justify-center lg:mb-[80px]"><Typography variant="body1">Failed to load celebrities. Please try again later.</Typography></div> : <CelebritiesList className="mt-6 md:mt-8" celebritiesList={paginatedCelebrities || []} onSearch={setSearchValue} />;
    $[106] = error;
    $[107] = isFetching;
    $[108] = isLoading;
    $[109] = paginatedCelebrities;
    $[110] = t34;
  } else {
    t34 = $[110];
  }
  let t35;
  if ($[111] !== activePage || $[112] !== handlePageChange || $[113] !== totalCelebrities || $[114] !== totalPages) {
    t35 = totalCelebrities > ITEMS_PER_PAGE && <div className="mt-6 md:mt-10"><PaginationControl page={activePage - 1} totalPages={totalPages} firstPage={() => handlePageChange(1)} lastPage={() => handlePageChange(totalPages)} prevPage={() => handlePageChange(activePage - 1)} nextPage={() => handlePageChange(activePage + 1)} setPage={next => handlePageChange(next + 1)} /></div>;
    $[111] = activePage;
    $[112] = handlePageChange;
    $[113] = totalCelebrities;
    $[114] = totalPages;
    $[115] = t35;
  } else {
    t35 = $[115];
  }
  let t36;
  if ($[116] !== t33 || $[117] !== t34 || $[118] !== t35) {
    t36 = <>{t33}{t34}{t35}</>;
    $[116] = t33;
    $[117] = t34;
    $[118] = t35;
    $[119] = t36;
  } else {
    t36 = $[119];
  }
  return t36;
};
export default HeroFrame;
function _temp(s) {
  return Number(s.trim());
}
function _temp2(n) {
  return !Number.isNaN(n) && n > 0;
}
function _temp3(value) {
  if (!value) {
    return;
  }
  const trimmed = value.trim();
  if (!trimmed.length) {
    return;
  }
  return value;
}
function _temp4(i) {
  return <CelebrityCardSkeleton key={i} />;
}